import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Type, Image as ImageIcon, Zap, Check, MapPin, User, Gift, Copy, Sparkles } from "lucide-react";
import api, { TIER_META, OCCASION_META } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function AddOnTile({ tid, active, onToggle, accent, emoji, title, desc, price }) {
  return (
    <button type="button" data-testid={tid} onClick={onToggle}
      className={`text-left rounded-sm border p-3 transition-all ${active ? "border-2" : "border-white/10 hover:border-white/25"}`}
      style={active ? { borderColor: accent, boxShadow: `0 0 18px ${accent}44` } : {}}>
      <div className="flex items-center justify-between">
        <span className="text-lg">{emoji}</span>
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: active ? accent : "#a1a1aa" }}>{price}</span>
      </div>
      <div className="text-sm font-bold mt-1.5" style={active ? { color: accent } : {}}>{title}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{desc}</div>
    </button>
  );
}

export default function Submit() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [mode, setMode] = useState(params.get("gift") === "1" ? "gift" : "self");
  const [pricing, setPricing] = useState(null);
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(null);
  const [detectedRegion, setDetectedRegion] = useState(null);
  const [contentType, setContentType] = useState("text");
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [tier, setTier] = useState("medium");
  const [boost, setBoost] = useState(false);
  const [submissionKind, setSubmissionKind] = useState("entertainment");
  const [aiRequested, setAiRequested] = useState(false);
  const [isPanel, setIsPanel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // gift fields
  const [recipientName, setRecipientName] = useState(params.get("to") || "");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [occasion, setOccasion] = useState("just_because");
  const [anonymous, setAnonymous] = useState(false);
  const [revealLink, setRevealLink] = useState(null);
  const [editRegion, setEditRegion] = useState(false);

  const loadPricing = async (r) => {
    const res = await api.get("/pricing", { params: r ? { region: r } : {} });
    setPricing(res.data);
    setRegions(res.data.regions);
    setRegion(res.data.region);
  };

  useEffect(() => {
    (async () => {
      try {
        const geo = await api.get("/geo");
        setDetectedRegion(geo.data.detected_region);
        await loadPricing(geo.data.detected_region);
      } catch (e) { await loadPricing(); }
    })();
  }, []);

  const activeTheme = pricing?.active_theme;
  const isGift = mode === "gift";

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4MB");
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const price = pricing ? pricing.tiers[tier] : 0;
  const boostFee = pricing ? pricing.boost_fee : 0;
  const symbol = pricing ? pricing.symbol : "";
  const PANEL_MULT = 2.5;
  const panelAddOn = price * (PANEL_MULT - 1);
  const panelFee = isPanel ? panelAddOn : 0;
  const total = price + panelFee + (boost ? boostFee : 0);

  const submit = async () => {
    if (contentType === "text" && !text.trim()) return toast.error("Write something to be roasted.");
    if (contentType === "image" && !image) return toast.error("Upload an image.");
    if (isGift && !recipientName.trim()) return toast.error("Who's this gift for? Add their name.");
    setSubmitting(true);
    try {
      const checkout = await api.post("/submissions/checkout", {
        content_type: contentType,
        content: contentType === "text" ? text : image,
        tier, boost, region, detected_region: detectedRegion,
        theme_id: activeTheme?.id || null,
        submission_kind: isGift ? "entertainment" : submissionKind,
        ai_requested: isGift ? false : aiRequested,
        is_panel: isGift ? false : isPanel,
        is_gift: isGift,
        recipient_name: isGift ? recipientName.trim() : null,
        recipient_email: isGift ? (recipientEmail.trim() || null) : null,
        occasion: isGift ? occasion : null,
        anonymous: isGift ? anonymous : false,
      });
      const co = checkout.data;
      const finalize = async (payment) => {
        const res = await api.post("/submissions/confirm", { draft_id: co.draft_id, ...payment });
        const d = res.data;
        if (d.status === "rejected") { toast.error("Blocked by moderation — that content crosses the guidelines."); return; }
        if (isGift && d.reveal_token) {
          setRevealLink(`${window.location.origin}/reveal/${d.reveal_token}`);
          toast.success("Gift is in the queue! 🎁");
          return;
        }
        if (d.status === "in_review") toast("Sent for a quick human review before it hits the queue.");
        else toast.success("Payment received — you're in the queue! 🔥");
        navigate("/app/inbox");
      };
      if (co.mock) { await finalize({}); }
      else {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load the payment window");
        const rzp = new window.Razorpay({
          key: co.key_id, amount: co.amount_minor, currency: co.currency, order_id: co.order_id,
          name: "RoastLit", description: `${tier} roast`,
          prefill: { name: co.name, email: co.email }, theme: { color: "#ff4500" },
          handler: async (resp) => { try { await finalize({ payment_id: resp.razorpay_payment_id, order_id: resp.razorpay_order_id, signature: resp.razorpay_signature }); } catch (e) { toast.error("Payment captured but confirmation failed — contact support."); } },
          modal: { ondismiss: () => { setSubmitting(false); toast("Payment cancelled."); } },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const copyLink = () => { navigator.clipboard?.writeText(revealLink)?.catch(() => {}); toast.success("Reveal link copied!"); };
  const shareGifterPost = () => {
    const cap = `I got ${recipientName || "my friend"} roasted for their ${(OCCASION_META[occasion] || {}).label?.toLowerCase() || "big day"} 🔥 roastlit.app`;
    if (navigator.share) navigator.share({ text: cap }).catch(() => {});
    else { navigator.clipboard?.writeText(cap)?.catch(() => {}); toast.success("Caption copied — post it!"); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">{isGift ? "Gift a Roast" : "Serve Yourself Up"}</h1>
      <p className="text-zinc-400 mt-2 mb-6">{isGift ? "Roast a friend — you pay, they get the reveal. You pick the winning roast." : "Drop your content, pick how hard you want it, pay, and enter the queue."}</p>

      <div className="grid grid-cols-2 gap-2 mb-6">
        <button data-testid="mode-self" onClick={() => setMode("self")}
          className={`flex items-center justify-center gap-2 py-3 rounded-sm border font-bold transition-colors ${!isGift ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
          <User size={16} /> Roast myself
        </button>
        <button data-testid="mode-gift" onClick={() => setMode("gift")}
          className={`flex items-center justify-center gap-2 py-3 rounded-sm border font-bold transition-colors ${isGift ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
          <Gift size={16} /> Gift a roast
        </button>
      </div>

      {activeTheme && (
        <div data-testid="submit-theme-banner" className="rounded-sm border border-brand/30 bg-brand/10 p-4 mb-6 flex items-center gap-3">
          <Sparkles className="text-brand shrink-0" size={18} />
          <div><div className="text-sm font-bold text-zinc-100">{activeTheme.label}</div>{activeTheme.description && <div className="text-xs text-zinc-400">{activeTheme.description}</div>}</div>
        </div>
      )}

      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-6 space-y-6">
        <div className="flex justify-end -mb-2" data-testid="region-chip-row">
          {!editRegion ? (
            <button data-testid="region-chip" onClick={() => setEditRegion(true)}
              className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 bg-white/5 border border-white/10 rounded-full px-2.5 py-1 hover:border-white/25 transition-colors">
              <MapPin size={11} className="text-zinc-500" /> {(regions.find((r) => r.code === region) || {}).currency || symbol || region}
              <span className="text-brand font-semibold">edit</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <select data-testid="region-select" value={region || ""} onChange={(e) => loadPricing(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 rounded-sm px-2.5 py-1.5 text-xs focus:border-brand focus:outline-none">
                {regions.map((r) => <option key={r.code} value={r.code}>{r.region_name} ({r.currency})</option>)}
              </select>
              <button data-testid="region-done" onClick={() => setEditRegion(false)} className="text-xs text-brand font-semibold">done</button>
            </div>
          )}
        </div>
        {editRegion && detectedRegion && region !== detectedRegion && (
          <div data-testid="override-note" className="text-[11px] text-warning text-right -mt-4">Overriding detected region ({detectedRegion})</div>
        )}
        {isGift && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs tracking-widest uppercase text-zinc-400 font-bold">Who's it for? *</label>
                <input data-testid="gift-recipient-name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Their name / handle"
                  className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-zinc-400 font-bold">Their email <span className="text-zinc-600 normal-case">(optional)</span></label>
                <input data-testid="gift-recipient-email" type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="So they get an in-app ping"
                  className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-zinc-400 font-bold">Occasion</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(OCCASION_META).map(([k, v]) => (
                  <button key={k} data-testid={`occasion-${k}`} onClick={() => setOccasion(k)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${occasion === k ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                    {v.emoji} {v.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer text-sm">
              <input data-testid="gift-anonymous" type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="accent-[#ff4500] w-4 h-4" />
              Gift anonymously (don't show my name on the reveal)
            </label>
          </div>
        )}

        {!isGift && (
          <div data-testid="submission-kind">
            <div className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-3">What kind of feedback?</div>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="kind-entertainment" onClick={() => setSubmissionKind("entertainment")}
                className={`text-left rounded-sm border p-3 transition-colors ${submissionKind === "entertainment" ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                <div className="text-sm font-bold">🔥 ROAST</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Full savage roast — bio, selfie, dating profile</div>
              </button>
              <button data-testid="kind-high_stakes" onClick={() => setSubmissionKind("high_stakes")}
                className={`text-left rounded-sm border p-3 transition-colors ${submissionKind === "high_stakes" ? "border-[#2dd4bf] bg-[#2dd4bf]/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                <div className="text-sm font-bold">🎤 VERDICT</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Honest verdict, less cruelty — resume, pitch, logo</div>
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-3">{isGift ? "What are we roasting them on?" : "What are we roasting?"}</div>
          <div className="grid grid-cols-2 gap-3">
            {[{ id: "text", label: "Text", icon: Type }, { id: "image", label: "Image", icon: ImageIcon }].map((c) => (
              <button key={c.id} data-testid={`ctype-${c.id}`} onClick={() => setContentType(c.id)}
                className={`flex items-center gap-2 justify-center py-3 rounded-sm border transition-colors ${contentType === c.id ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                <c.icon size={16} /> {c.label}
              </button>
            ))}
          </div>
        </div>

        {contentType === "text" ? (
          <div>
            {!isGift && (
              <div className="flex flex-wrap gap-2 mb-2.5" data-testid="prompt-chips">
                {[
                  { label: "Dating bio", seed: "My dating bio: " },
                  { label: "Resume line", seed: "A line from my resume: " },
                  { label: "Startup pitch", seed: "My startup pitch: " },
                  { label: "Instagram bio", seed: "My Instagram bio: " },
                  { label: "My selfie caption", seed: "The caption on my selfie: " },
                ].map((p) => (
                  <button key={p.label} type="button" data-testid={`prompt-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setText(p.seed)}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:border-brand/60 hover:text-white transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            )}
            <textarea data-testid="submit-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={500}
              placeholder={isGift ? "Describe your friend, the occasion, an inside joke to roast…" : "Tap an example above, or write your own — a bio, a resume line, a pitch…"}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-sm focus:border-brand focus:outline-none resize-none" />
          </div>
        ) : (
          <div>
            <input data-testid="submit-image" type="file" accept="image/*" onChange={onFile} className="text-sm text-zinc-400 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-brand file:text-black file:font-semibold" />
            {image && <img src={image} alt="preview" className="mt-3 max-h-56 rounded-sm border border-white/10" />}
          </div>
        )}

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-3">Choose the heat</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {pricing && Object.keys(pricing.tiers).map((k) => {
              const active = tier === k; const meta = TIER_META[k];
              return (
                <button key={k} data-testid={`tier-${k}`} onClick={() => setTier(k)}
                  className={`text-left rounded-sm border p-3 transition-all ${active ? "border-2" : "border-white/10 hover:border-white/25"}`}
                  style={active ? { borderColor: meta.color, boxShadow: `0 0 18px ${meta.color}55` } : {}}>
                  <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: meta.color }}>{meta.label}</div>
                  <div className="font-heading text-2xl font-bold">{symbol}{pricing.tiers[k]}</div>
                </button>
            );
          })}
          </div>
        </div>

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-3">Add-ons</div>
          <div className={`grid gap-3 ${isGift ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3"}`}>
            <AddOnTile tid="boost-checkbox" active={boost} onToggle={() => setBoost((v) => !v)} accent="#ff4500"
              emoji="⚡" title="Boost" desc="Jump higher in the roaster queue" price={`+${symbol}${boostFee}`} />
            {!isGift && (
              <AddOnTile tid="ai-checkbox" active={aiRequested} onToggle={() => setAiRequested((v) => !v)} accent="#a855f7"
                emoji="🤖" title="AI comparison" desc="See if a human out-roasts the machine 💀" price="Free" />
            )}
            {!isGift && (
              <AddOnTile tid="panel-checkbox" active={isPanel} onToggle={() => setIsPanel((v) => !v)} accent="#2dd4bf"
                emoji="🎤" title="Panel Verdict" desc="A compiled panel of 4 roasters, not one winner" price={`+${symbol}${panelAddOn.toFixed(2)}`} />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Total</div>
            <div className="font-heading text-3xl font-bold">{symbol}{total.toFixed(2)}</div>
          </div>
          <button data-testid="submit-pay-btn" onClick={submit} disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
            {submitting ? "Processing…" : <>{isGift ? "Pay & Gift" : "Pay & Submit"} <Check size={16} /></>}
          </button>
        </div>
        <p className="text-[11px] text-zinc-600">Mock payment for testing — no real charge.</p>
      </div>

      <Dialog open={!!revealLink} onOpenChange={(o) => { if (!o) { setRevealLink(null); navigate("/app/inbox"); } }}>
        <DialogContent data-testid="gift-reveal-dialog" className="bg-zinc-950 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">Gift is in the queue 🎁</DialogTitle>
            <DialogDescription>Roasters are on it. Once you pick the winning roast in your Inbox, send {recipientName || "them"} this reveal link — it shows only the winner you chose.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-sm border border-white/10 bg-zinc-900 p-2">
            <input data-testid="reveal-link-input" readOnly value={revealLink || ""} className="flex-1 bg-transparent text-xs text-zinc-300 outline-none" />
            <button data-testid="copy-reveal-link" onClick={copyLink} className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white"><Copy size={15} /></button>
          </div>
          <button data-testid="gifter-share-btn" onClick={shareGifterPost} className="w-full py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-sm font-bold text-white transition-colors">
            Share "I got them roasted" to my story
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
